/** Camera access needs a secure context. Plain http over the LAN will not do. */
export function cameraAvailable(): { ok: boolean; reason?: string } {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return { ok: false, reason: "This browser has no camera access." };
  }
  if (typeof window !== "undefined" && !window.isSecureContext) {
    return {
      ok: false,
      reason:
        "The camera needs a secure connection. Open the app at its https address — a plain http address on your network will not work.",
    };
  }
  return { ok: true };
}

/**
 * Ask for the sharpest rear-facing stream available.
 *
 * Resolution is the single biggest factor for a 17-character Code 39 VIN.
 * Unsupported keys are ignored; unknown top-level constraints must not be
 * mixed into the video object or some browsers reject the whole request.
 */
export const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  audio: false,
  video: {
    facingMode: { ideal: "environment" },
    width: { ideal: 1920 },
    height: { ideal: 1080 },
  },
};

export async function startCamera(): Promise<MediaStream> {
  const stream = await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);
  const track = stream.getVideoTracks()[0];
  if (track) {
    try {
      const caps = track.getCapabilities?.() as
        | { focusMode?: string[]; torch?: boolean; zoom?: { min: number; max: number } }
        | undefined;
      const advanced: Record<string, unknown>[] = [];
      if (caps?.focusMode?.includes("continuous")) advanced.push({ focusMode: "continuous" });
      if (caps?.zoom && caps.zoom.max > (caps.zoom.min ?? 0)) {
        const mid = Math.min(caps.zoom.max, Math.max(caps.zoom.min, 1.4));
        advanced.push({ zoom: mid });
      }
      if (advanced.length) {
        await track.applyConstraints({ advanced } as unknown as MediaTrackConstraints);
      }
    } catch {
      // Capabilities are optional. A stream without them still works.
    }
  }
  return stream;
}

export async function playVideo(video: HTMLVideoElement, stream: MediaStream): Promise<void> {
  video.srcObject = stream;
  video.setAttribute("playsinline", "true");
  video.muted = true;
  video.autoplay = true;
  try {
    await video.play();
  } catch {
    // iOS sometimes needs a second kick after metadata.
    await new Promise<void>((resolve) => {
      video.onloadedmetadata = () => {
        video.play().catch(() => {});
        resolve();
      };
      setTimeout(() => resolve(), 400);
    });
  }
}

export async function setTorch(track: MediaStreamTrack, on: boolean): Promise<boolean> {
  try {
    const caps = track.getCapabilities?.() as { torch?: boolean } | undefined;
    if (!caps?.torch) return false;
    await track.applyConstraints(
      { advanced: [{ torch: on }] } as unknown as MediaTrackConstraints,
    );
    return true;
  } catch {
    return false;
  }
}

export function stopStream(stream: MediaStream | null) {
  stream?.getTracks().forEach((t) => t.stop());
}
